import { Injectable, OnDestroy } from '@angular/core';
import { Store, Action, MemoizedSelector } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, take, map, tap } from 'rxjs/operators';

/**
 * An abstract base class for services that manage entities by checking the
 * NgRx store before making an API call.
 *
 * The implementing class must provide an instance of the NgRx Store.
 */
@Injectable({
  providedIn: 'root'
})
export class EntityFetcherService implements OnDestroy {

  private destroy$ = new Subject<void>();

  constructor(private store: Store<any>) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * A generic function to get an entity by its ID.
   *
   * It follows this logic:
   * 1. Checks the NgRx store using the provided selector.
   * 2. If the entity exists, it returns it immediately.
   * 3. If not, it fetches it from the API using the provided function and filter.
   * 4. Upon successful API fetch, it dispatches the provided action to store the entity.
   * 5. Returns the entity from the API call.
   *
   * @template T The type of the entity, which must have an 'id' property.
   * @param id The ID of the entity to retrieve.
   * @param selector A memoized NgRx selector that takes an ID and returns the entity from the store.
   * @param apiFn The API function to call (e.g., `someService.find`). It must return an Observable of an array of entities.
   * @param additionalFilter An object containing extra filter properties, such as 'include' clauses, to be merged with the ID filter.
   * @param successAction A function that takes the fetched entity and returns an NgRx Action to be dispatched for saving it to the store.
   * @returns An Observable of the entity or null if not found.
   */
  public findItemById<T extends { id: string }>(
    id: string,
    selector: (id: string) => MemoizedSelector<object, T | undefined | null>,
    apiFn: (params: any) => Observable<T[]>,
    additionalFilter: Record<string, any>,
    successAction: (data: T) => Action
  ): Observable<T | null> {
    // 1. Select the entity from the store.
    return this.store.select(selector(id)).pipe(
      // 2. Take only the current value from the stream, then complete.
      take(1),
      // 3. Switch to a new observable based on the store's result.
      switchMap((itemFromStore) => {
        // 4. If the item was found in the store, return it wrapped in an `of()` observable.
        if (itemFromStore) {
          return of(itemFromStore);
        }

        // 5. If not in the store, construct the API filter and fetch from the API.
        const apiFilter = {
          ...additionalFilter,

          where: additionalFilter?.where ? {
            and: [
              { id: id },
              ...additionalFilter.where
            ]
          } : {
            id: id
          },
        };

        return apiFn({ filter: apiFilter }).pipe(
          // 6. The API returns an array; map it to the first element or null.
          map((response: T[]) => response?.[0] || null),
          // 7. Use `tap` for the side effect of dispatching the action to the store.
          tap((itemFromApi) => {
            if (itemFromApi) {
              // 8. Dispatch the provided success action with the fetched item.
              if (successAction) {
                this.store.dispatch(successAction(itemFromApi));
              }
            }
          })
        );
      })
    );
  }

  /**
   * A generic function to get a list of entities.
   *
   * It follows this logic:
   * 1. Checks the NgRx store using the provided selector to see if the list already exists.
   * 2. If the list is present (not null and not empty), it returns it immediately.
   * 3. If not, it fetches the list from the API using the provided function and filter.
   * 4. Upon successful API fetch, it dispatches the provided action to store the list.
   * 5. Returns the list of entities from the API call.
   *
   * @template T The type of the entity.
   * @param selector A memoized NgRx selector that returns the array of entities from the store.
   * @param apiFn The API function to call (e.g., `someService.find`). It must return an Observable of an array of entities.
   * @param filter An object containing filter properties (e.g., 'include', 'where').
   * @param successAction A function that takes the fetched entity array and returns an NgRx Action to be dispatched.
   * @returns An Observable of the entity array.
   */
  public getItems<T>(
    selector: MemoizedSelector<object, T[]>,
    apiFn: (params: any) => Observable<T[]>,
    filter: object,
    successAction: (data: T[]) => Action
  ): Observable<T[]> {
    // 1. Select the list of items from the store.
    return this.store.select(selector).pipe(
      // 2. Take only the current value from the stream, then complete.
      take(1),
      // 3. Switch to a new observable based on the store's result.
      switchMap((itemsFromStore) => {
        // 4. If the list exists and has items, return it wrapped in an `of()` observable.
        if (itemsFromStore && itemsFromStore.length > 0) {
          return of(itemsFromStore);
        }

        // 5. If not in the store, fetch from the API.
        return apiFn({ filter }).pipe(
          // 6. Use `tap` for the side effect of dispatching the action to the store.
          tap((itemsFromApi) => {
            if (itemsFromApi) {
              // 7. Dispatch the provided success action with the fetched items.
              if (successAction) {
                this.store.dispatch(successAction(itemsFromApi));
              }
            }
          })
        );
      })
    );
  }

}

